import { useCallback } from "react";

import { useQueryClient } from "@tanstack/react-query";

type Exact<T, U> = T extends U ? (U extends T ? T : never) : never;

/**
 * A custom hook that creates a client mutation function for updating query data in React Query.
 *
 * @param queryKey - The key of the query to be updated in the React Query cache.
 * @param _apiFn - The API function that fetches data (not used directly in this hook).
 * @param mapFn - A function that maps the current query data to a new structure. This function takes the current data
 *                and returns another function that accepts additional parameters to produce the updated data.
 *
 * @returns An object containing the `clientMutation` function, which can be used to update the query data in the cache.
 */
export const useCreateClientMutation = <
  K extends readonly unknown[],
  ApiFnParams extends unknown[],
  R,
  P extends unknown[],
  M extends NonNullable<Awaited<R>>,
>(
  queryKey: K,
  _apiFn: (...apiFnParams: ApiFnParams) => R,
  mapFn: (
    data: NonNullable<Awaited<R>>,
    // FIXME: works only for 1 level deep
  ) => (...args: P) => Exact<M, NonNullable<Awaited<R>>>,
) => {
  const queryClient = useQueryClient();

  const clientMutation = useCallback(
    (...args: P) => {
      const data = queryClient.getQueryData<Awaited<R>>(queryKey);

      if (!data) return;

      const newData = mapFn(data)(...args);

      queryClient.setQueryData(queryKey, () => {
        return newData;
      });
    },
    [mapFn, queryClient, queryKey],
  );

  return { clientMutation };
};
